import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  notification,
  Card,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Statistic,
  Spin,
  Tooltip
} from "antd";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { 
  BankOutlined, 
  UserOutlined, 
  PlusOutlined, 
  EditOutlined,
  TeamOutlined,
  HomeOutlined,
  EnvironmentOutlined 
} from "@ant-design/icons";
import "./Styles.css";

// Custom theme colors for Zambian fintech (copied from home.jsx)
const ZAMBIA_THEME = {
  primary: "#078930", // Zambian green
  secondary: "#EF3340", // Zambian red
  accent: "#FFB81C", // Gold accent
  neutral: "#2D2D2D", // Dark neutral
};

const { Title, Text } = Typography;
const { Option } = Select;

const Branches = () => {
  const token = localStorage.getItem("token");
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingBranch, setEditingBranch] = useState(null);
  const { company } = useContext(AppContext);
  
  // Column configuration for branches table
  const columns = [
    {
      title: "Branch Name",
      dataIndex: "branch_name",
      render: (text) => (
        <Space>
          <BankOutlined style={{ color: ZAMBIA_THEME.primary }} />
          <span style={{ fontWeight: "bold" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: "Branch Manager",
      render: (_, record) => {
        // Find the manager in the users array
        const manager = users.find(user => user.id === record.branch_manager);
        return manager ? (
          <Space>
            <UserOutlined style={{ color: ZAMBIA_THEME.accent }} />
            {`${manager.first_name} ${manager.last_name}`}
          </Space>
        ) : (
          <Tag color="orange">No Manager Assigned</Tag>
        );
      },
    },
    {
      title: "Number of Loans",
      render: (_, record) => (
        <Tag color={ZAMBIA_THEME.primary}>
          {record.loans?.length || 0} Loans
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "published" ? ZAMBIA_THEME.primary : ZAMBIA_THEME.secondary}>
          {status === "published" ? "Active" : status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Branch">
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
              style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
            />
          </Tooltip>
          <Tooltip title="View Branch Details">
            <Button 
              icon={<TeamOutlined />} 
              onClick={() => notification.info({
                message: "Feature Coming Soon",
                description: "Branch detail view will be available in a future update."
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch branches and users in parallel
        const [branchesResponse, usersResponse] = await Promise.all([
          axios.get(
            `https://zedlenders.pickmesms.com/items/branches?filter[company]=${company?.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          axios.get(
            `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_neq]=Borrower&filter[company][_eq]=${company?.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        ]);
        
        setBranches(branchesResponse.data.data);
        setUsers(usersResponse.data.data);
      } catch (err) {
        notification.error({
          message: "Error",
          description: "Failed to fetch branch data. Please check your connection.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company?.id, token]);

  const handleCreate = () => {
    form.resetFields();
    setEditingBranch(null);
    setIsModalVisible(true);
  };

  const handleEdit = (branch) => {
    form.setFieldsValue({
      ...branch,
      branch_name: branch.branch_name,
      branch_manager: branch.branch_manager,
      status: branch.status,
    });
    setEditingBranch(branch);
    setIsModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = editingBranch
        ? `https://zedlenders.pickmesms.com/items/branches/${editingBranch.id}`
        : `https://zedlenders.pickmesms.com/items/branches`;

      const method = editingBranch ? "patch" : "post";
      
      // Add company ID if creating a new branch
      const payload = editingBranch 
        ? values 
        : { ...values, company: company.id };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      notification.success({
        message: "Success",
        description: `Branch ${editingBranch ? "updated" : "created"} successfully!`,
      });

      setIsModalVisible(false);
      
      // Refresh branches after adding/editing
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/branches?filter[company]=${company?.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBranches(response.data.data);
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.errors?.[0]?.message || "Failed to save branch",
      });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}>
        <Spin size="large" tip="Loading branches..." />
      </div>
    );
  }

  // Get current user name from local storage for welcome message
  const userName = localStorage.getItem("userName") || "User";

  return (
    <div className="dashboard-container">
      <Card
        className="welcome-banner"
        style={{
          background: `linear-gradient(135deg, ${ZAMBIA_THEME.primary}40, ${ZAMBIA_THEME.accent}30)`,
          marginBottom: 24,
          borderRadius: 8,
          border: `1px solid ${ZAMBIA_THEME.primary}20`,
        }}
      >
        <Row align="middle" gutter={[24, 0]}>
          <Col>
            <BankOutlined style={{ fontSize: 36, color: ZAMBIA_THEME.primary }} />
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0, color: ZAMBIA_THEME.neutral }}>
              Branch Management
            </Title>
            <Text>Manage your loan offices across Zambia</Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]} className="metric-row">
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Total Branches"
              value={branches.length}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Active Branches"
              value={branches.filter(b => b.status === "published").length}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<HomeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card className="metric-card" bordered={false}>
            <Statistic
              title="Branch Managers"
              value={new Set(branches.map(b => b.branch_manager).filter(Boolean)).size}
              valueStyle={{ color: ZAMBIA_THEME.primary, fontSize: 24 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <EnvironmentOutlined style={{ color: ZAMBIA_THEME.primary }} />
            <span>Branch Network</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
          >
            Add New Branch
          </Button>
        }
        className="branch-card"
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={branches}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <BankOutlined style={{ color: ZAMBIA_THEME.primary }} />
            {editingBranch ? "Edit Branch" : "Create New Branch"}
          </Space>
        }
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Branch Name"
            name="branch_name"
            rules={[{ required: true, message: "Please enter branch name" }]}
          >
            <Input prefix={<BankOutlined />} placeholder="E.g., Main Branch, Lusaka Downtown, etc." />
          </Form.Item>

          <Form.Item
            label="Branch Manager"
            name="branch_manager"
            rules={[{ required: true, message: "Please select a branch manager" }]}
          >
            <Select 
              placeholder="Select branch manager"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option.children.toString();
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              prefix={<UserOutlined />}
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {`${user.first_name} ${user.last_name}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: "Please select branch status" }]}
            initialValue="published"
          >
            <Select>
              <Option value="published">Active</Option>
              <Option value="draft">Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Branch Address"
            name="branch_address"
            rules={[{ required: false }]}
          >
            <Input.TextArea 
              placeholder="Enter branch address (optional)" 
              rows={3}
              prefix={<EnvironmentOutlined />} 
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{ background: ZAMBIA_THEME.primary, borderColor: ZAMBIA_THEME.primary }}
            >
              {editingBranch ? "Update Branch" : "Create Branch"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Branches;